<template>
    <ModeSwitcher
        ref="modeSwitcher"
        :main-mode-text="$t('create')"
        :create-mode-text="$t('create_new_communication')"
        :edit-mode-text="$t('edit_communication')"
        :is-hide-back-button="isHideBackButton"
        @reset="resetData"
    >
        <template v-slot:additional-mode>
            <communication-edit-form
                ref="form"
                v-model="communicationData"
                v-model:create-new-order="createNewOrder"
                :is-create-mode="isCreateMode"
                :_valid="valid"
                :services="services"
                @change-valid="valid = $event"
            />
            <v-btn color="primary" :loading="loading" :disabled="!valid" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn v-if="!isHideBackButton" variant="text" @click="goToTable">
                {{ $t('cancel') }}
            </v-btn>
        </template>
        <template v-slot:main-mode>
            <communications-calendar
                v-if="isCalendarMode"
                :communications="communications"
                :communications-loading="communicationsLoading"
                :create-communication-dialog="loading"
                @edit-communication="editCommunication"
                @add-communication-filters="addCommunicationFilters"
                @get-communications="getCommunications"
            />
            <slot :get-communications="getCommunications" />
            <v-data-table-server
                v-if="!isCalendarMode"
                :headers="headers"
                :items="communications"
                :loading="loading || communicationsLoading"
                :items-length="communicationsTotalCount"
                :items-per-page-options="[10, 15, 50, -1]"
                :options="tableOptions"
                :hide-default-header="isMobile"
                :hide-default-footer="!communicationsTotalCount && communications.length <= tableOptions.itemsPerPage"
                disable-sort
                data-cy="communications-table"
                class="elevation-1 table-server"
                @update:options="onChangeTableOptions"
            >
                <template v-slot:item.communicationDateTime="{item}">
                    {{ $filters.dateTimeFormat(item.communicationDateTime) }}
                </template>
                <template v-slot:item.accountName="{item}">
                    <v-icon v-if="item.accountType" size="small">
                        {{ getAccountIcon(item.accountType) }}
                    </v-icon>
                    {{ item.accountName ? item.accountName : '' }}
                    <div
                        v-if="
                            ['CORPORATE_CLIENT', 'AGENCY_DISTRIBUTOR'].includes(item.accountType) &&
                            item.contactPersonFullName
                        "
                        class="text-caption"
                    >
                        {{ item.contactPersonFullName }}
                    </div>
                </template>
                <template v-slot:item.assignedUser="{item}">
                    {{ item.assignedUser ? userName(item.assignedUser) : '' }}
                </template>
                <template v-slot:item.communicationType="{item}">
                    {{ $t(`communicationType.${item.communicationType}`) }}
                </template>
                <template v-slot:item.communicationStage="{item}">
                    {{ item.communicationStage ? $t(`communicationStage.${item.communicationStage}`) : '' }}
                </template>
                <template v-slot:item.recipientToMails="{item}">
                    {{ item.recipientToMails ? item.recipientToMails.join('\n') : '' }}
                </template>
                <template v-slot:item.status="{item}">
                    <v-switch
                        :model-value="item.status === 'ACTIVE'"
                        color="primary"
                        hide-details
                        :data-cy="`communication-status-${item.communicationId}`"
                        @click="switchCommunicationActive(item)"
                    >
                        <template v-slot:label>
                            <span class="text-no-wrap" style="white-space: nowrap">
                                {{ $t('active') }}
                            </span>
                        </template>
                    </v-switch>
                </template>
                <template v-slot:item.edit="{item}">
                    <v-btn
                        :data-cy="`link-edit-communication-${item.communicationId}`"
                        color="primary"
                        size="small"
                        icon="mdi-pencil"
                        variant="text"
                        @click="editCommunication(item)"
                    />
                </template>
                <template v-slot:item.mobile="{item}">
                    <div class="d-flex align-start full-width py-1">
                        <div class="d-flex flex-column text-caption">
                            <span>
                                {{ $filters.dateTimeFormat(item.communicationDateTime) }}
                            </span>
                            <span>
                                {{ $t(`communicationType.${item.communicationType}`) }}
                            </span>
                            <span v-if="item.recipientToMails && item.recipientToMails.length">
                                <span class="font-weight-medium"> {{ $t('email_form.to') }}: </span>
                                <span>
                                    {{ item.recipientToMails ? item.recipientToMails.join('\n') : '' }}
                                </span>
                            </span>
                            <span>
                                {{ item.subject }}
                            </span>
                            <span>
                                <span class="font-weight-medium"> {{ $t('assigned_to') }}: </span>
                                <span>
                                    {{ item.assignedUser ? userName(item.assignedUser) : '' }}
                                </span>
                            </span>
                            <span v-if="item.reservationNumber">
                                <span class="font-weight-medium"> {{ $t('order') }}: </span>
                                {{ item.reservationNumber }}
                            </span>
                            <span>
                                <span class="font-weight-medium"> {{ $t('stage') }}: </span>
                                {{ item.communicationStage ? $t(`communicationStage.${item.communicationStage}`) : '' }}
                            </span>
                        </div>
                        <v-spacer />
                        <div class="d-flex flex-column full-height align-end text-caption">
                            <div class="d-flex align-center">
                                <v-icon v-if="item.accountType" size="small">
                                    {{ getAccountIcon(item.accountType) }}
                                </v-icon>
                                {{ item.accountName ? item.accountName : '' }}
                                <span
                                    v-if="
                                        ['CORPORATE_CLIENT', 'AGENCY_DISTRIBUTOR'].includes(item.accountType) &&
                                        item.contactPersonFullName
                                    "
                                    class="text-caption"
                                >
                                    {{ item.contactPersonFullName }}
                                </span>
                            </div>
                            <div class="d-flex align-center mt-1">
                                <span class="text-no-wrap text-caption">
                                    {{ $t('active') }}
                                </span>
                                <v-switch
                                    :model-value="item.status === 'ACTIVE'"
                                    color="primary"
                                    hide-details
                                    class="mx-2"
                                    density="compact"
                                    :data-cy="`communication-status-${item.communicationId}`"
                                    @click="switchCommunicationActive(item)"
                                />
                            </div>
                            <v-btn
                                :data-cy="`link-edit-communication-${item.communicationId}`"
                                color="primary"
                                size="small"
                                icon="mdi-pencil"
                                variant="text"
                                @click="editCommunication(item)"
                            />
                        </div>
                    </div>
                </template>
            </v-data-table-server>
        </template>
    </ModeSwitcher>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Vue, Inject} from 'vue-facing-decorator'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import TimeField from '@/components/snippets/forms/TimeField.vue'
    import CommunicationEditForm from '@/components/account/communications/CommunicationEditForm.vue'
    import CommunicationsCalendar from '@/components/account/communications/CommunicationsCalendar.vue'
    import type {CommunicationType} from '~/utils/types/account/communications/types'

    const blankCommunication = (orderId = null) => ({
        type: null,
        stage: null,
        communicationDateTime: null,
        subject: null,
        description: null,
        assignedToId: null,
        orderId,
        accountId: null,
        accountType: null,
        contactPersonFullName: null,
        contactId: null,
        processId: null,
        visibleForSuppliers: null,
        active: true,
        communicationId: null,
        recipientFromMails: null,
        recipientToMails: null,
        recipientCCMails: null,
        recipientBCCMails: null,
    })

    @Component({
        components: {
            CommunicationsCalendar,
            CommunicationEditForm,
            TimeField,
            DatePicker,
        },
    })
    export class Communications extends Vue {
        @Prop() orderId: number
        @Prop({default: '0'}) elevation
        @Prop({default: []}) communications: CommunicationType[]
        @Prop() communicationsLoading: boolean
        @Prop() services
        @Prop({default: 0, type: Number}) communicationsTotalCount: number
        @Prop({default: false, type: Boolean}) createModeOnly: boolean
        @Prop({default: false, type: Boolean}) isHideBackButton: boolean
        @Prop({default: false, type: Boolean}) isCalendarMode: boolean
        @Prop() communicationDataForCreate

        @Inject() goTo
        @Emit()
        getCommunications() {
            if (this.isCalendarMode) {
                return {}
            } else {
                return this.tableOptions
            }
        }

        loading: boolean = false
        valid: boolean = false
        communicationData = blankCommunication()
        tableOptions = {
            itemsPerPage: 10,
            page: 1,
        }
        createNewOrder = false

        mounted() {
            this.resetData()
        }

        onChangeTableOptions(val) {
            this.tableOptions = val
            this.getCommunications()
        }

        async newOrder() {
            if (this.createNewOrder && !this.orderId) {
                const rq = {
                    clientPriceType: 'ONLY_TOTAL',
                    clientId: this.communicationData.accountId,
                }
                try {
                    const {
                        orders: [{orderId}],
                    } = await this.$api.orders.post(rq)
                    this.communicationData.orderId = orderId
                } catch {
                    /* empty */
                }
            }
        }

        @Emit()
        addCommunicationFilters(newFilters) {
            return newFilters
        }

        @Emit()
        async sendCreateCommunication() {
            try {
                this.loading = true
                await this.newOrder()
                await this.$api.communications.post(this.requestData)
                this.getCommunications()

                if (
                    this.$conf.public.reservationReport &&
                    this.requestData.visibleForSuppliers &&
                    this.requestData.processId
                ) {
                    await this.markAsNotSentToSupplier()
                }

                this.$toast.success(this.$t('success_message.saved_successfully'))
                this.goToTable()
            } catch (e) {
                if (e.msg) {
                    this.$toast.error(e.msg)
                } else {
                    this.$toast.error(this.$t('error_message.save_error'))
                }
            } finally {
                this.loading = false
            }
        }

        async sendEditCommunication() {
            try {
                this.loading = true
                await this.newOrder()
                await this.$api.communications.put(this.requestData.communicationId, this.requestData)
                if (
                    this.$conf.public.reservationReport &&
                    this.requestData.visibleForSuppliers &&
                    this.requestData.processId
                ) {
                    await this.markAsNotSentToSupplier()
                }
                this.getCommunications()
                this.goToTable()
            } catch (e) {
                if (e.msg) {
                    this.$toast.error(e.msg)
                }
            } finally {
                this.loading = false
            }
        }

        async save(): Promise<void> {
            const {valid} = await this.$refs.form.validate()
            if (!valid) return
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            this.isEditMode ? await this.sendEditCommunication() : await this.sendCreateCommunication()
            this.resetData()
        }

        async markAsNotSentToSupplier(): Promise<void> {
            await this.$api.orders.put(this.orderId, {
                servicesInfo: [
                    {
                        processId: this.requestData?.processId,
                        serviceSentToSupplier: false,
                    },
                ],
            })
            await runtimeStore.refreshOrder()
        }

        resetData(): void {
            this.communicationData = blankCommunication(this.orderId)
        }

        async editCommunication(e): Promise<void> {
            await this.setCommunicationData(e)
            this.$refs.modeSwitcher.edit()
            this.goTo(0)
        }

        async setCommunicationData(e): Promise<void> {
            let services = this.services
            if (!services && e.reservationNumber) {
                try {
                    services = (
                        await this.$api.orders.get({
                            orderType: authStore.orderType,
                            orderId: +e.reservationNumber,
                            responseType: 'SHORT',
                        })
                    ).orders[0].services
                } catch (e) {
                    services = []
                }
            }
            this.communicationData = {
                assignedToId: e.assignedUser?.id,
                accountId: e.accountId,
                accountType: e.accountType,
                contactPersonFullName: e.contactPersonFullName,
                stage: e.communicationStage,
                communicationDateTime: e.communicationDateTime?.split(' ').join('T'),
                subject: e.subject,
                description: e.description,
                orderId: +e.reservationNumber,
                active: e.status === 'ACTIVE',
                communicationId: e.communicationId,
                contactId: e.contactId,
                processId: services?.find(item => item.serviceId === e.serviceId)?.processId,
                type: e.communicationType,
                ...(e.recipientFromMails && {recipientFromMails: e.recipientFromMails}),
                ...(e.recipientToMails && {recipientToMails: e.recipientToMails}),
                ...(e.recipientCCMails && {recipientCCMails: e.recipientCCMails}),
                ...(e.recipientBCCMails && {recipientBCCMails: e.recipientBCCMails}),
                visibleForSuppliers: e.visibleForSuppliers,
            }
        }

        async switchCommunicationActive(item): Promise<void> {
            item.status = item.status !== 'ACTIVE' ? 'ACTIVE' : 'INACTIVE'
            await this.setCommunicationData(item)
            await this.sendEditCommunication()
        }

        @Emit()
        goToTable(): void {
            this.resetData()
            if (!this.isHideBackButton) {
                this.$refs.modeSwitcher.goToTable()
            }
        }

        userName(user): string {
            if (!user) return ''
            const {firstName, lastName} = user
            return `${firstName || ''} ${lastName || ''}`.trim()
        }

        create(): void {
            this.goTo(0)
            if (this.communicationDataForCreate) {
                this.communicationData = {...blankCommunication(this.orderId), ...this.communicationDataForCreate}
            }

            this.$refs.modeSwitcher.create()
        }

        get requestData() {
            return {
                accountId: this.communicationData.accountId,
                accountType: this.communicationData.accountType,
                type: this.communicationData.type,
                stage: this.communicationData.stage,
                communicationDateTime: this.communicationData.communicationDateTime,
                subject: this.communicationData.subject,
                description: this.communicationData.description,
                orderId: this.communicationData.orderId,
                active: this.communicationData.active,
                ...(this.communicationData.communicationId && {
                    communicationId: this.communicationData.communicationId,
                }),
                ...(this.communicationData.processId && {processId: this.communicationData.processId}),
                ...(this.communicationData.contactId && {contactId: this.communicationData.contactId}),
                ...(this.communicationData.assignedToId && {assignedToId: this.communicationData.assignedToId}),
                ...(this.communicationData.recipientFromMails && {
                    recipientFromMails: this.communicationData.recipientFromMails,
                }),
                ...(this.communicationData.recipientToMails && {
                    recipientToMails: this.communicationData.recipientToMails,
                }),
                ...(this.communicationData.recipientCCMails && {
                    recipientCCMails: this.communicationData.recipientCCMails,
                }),
                ...(this.communicationData.recipientBCCMails && {
                    recipientBCCMails: this.communicationData.recipientBCCMails,
                }),
                visibleForSuppliers: this.communicationData.visibleForSuppliers,
            }
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get headers() {
            if (this.isMobile) return [{value: 'mobile'}]

            return [
                {title: `${this.$t('date')}/${this.$t('time')}`, value: 'communicationDateTime'},
                {title: this.$t('account'), value: 'accountName'},
                {title: this.$t('assigned_to'), value: 'assignedUser'},
                {title: this.$t('type'), value: 'communicationType'},
                {title: this.$t('recipient_email'), value: 'recipientToMails'},
                {title: this.$t('subject'), value: 'subject'},
                {title: this.$t('order'), value: 'reservationNumber'},
                {title: this.$t('stage'), value: 'communicationStage'},
                {title: this.$t('status'), value: 'status'},
                {title: this.$t('edit'), value: 'edit'},
            ]
        }

        get isCreateMode() {
            return this.$refs.modeSwitcher?.createMode
        }

        get isEditMode() {
            return this.$refs.modeSwitcher?.editMode
        }

        getAccountIcon(accountType: string) {
            switch (accountType) {
                case 'PRIVATE_CLIENT':
                    return 'mdi-human-male-female'
                case 'CORPORATE_CLIENT':
                    return 'mdi-account-tie'
                case 'SUPPLIER':
                    return 'mdi-account-multiple-outline'
                default:
                    return 'mdi-brightness-auto'
            }
        }
    }

    export default Communications
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';
    :deep(div) {
        td:last-of-type {
            min-width: 60px;
        }
    }
</style>
